/* eslint-disable @typescript-eslint/no-explicit-any */
import { prePageCall } from '@marriott/mi-headless-utils';
import { GetServerSideProps } from 'next';
//This returns a component which can be rendered in any product specific [[page.tsx]]
//to return the page template(Head, main, sections) along with the required react Components mentioned in model.json and
// load the CSS and JS libs required by static components from AEM.
import { ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { Layout, respGridUtil } from '@marriott/mi-headless-utils';
import { Types } from '@marriott/mi-ui-library';
import clsx from 'clsx';
import { getDeployedEnvType, getNextMiApolloClient } from '@marriott/mi-apollo-client-utils';
import { phoenixCobrandECMQuery } from '@marriott/mi-cobrand-graphql';
import { logger } from '@marriott/mi-headless-utils';
import { fetchFlag, FEATURE_FLAGS, TARGETS } from '@marriott/mi-feature-flag-utils';
import { envVarKeys } from '../_constants/envVarKeys';
import { ECM_ENDPOINT } from '../_constants/constants';
import operationSignatures from '../_constants/operationSignatures.json';
import {
  buildRedirectResponse,
  checkEcmEligibility,
  isMetaInAppUserAgent,
  transformCobrandInterstitialForMetaInApp,
} from '../helper';
const { log } = logger({})('credit-cards.tsx');

interface CreditCardsLandingPageProps {
  model: any;
  pagePath: string;
  isAuthorMode: boolean;
}

export default function CreditCardsLandingPage({ model, pagePath, isAuthorMode }: CreditCardsLandingPageProps) {
  const {
    metaNames,
    metaProperties,
    title,
    staticComponentClientLibsCSS,
    staticComponentClientLibsJS,
    gridCss,
    canonical,
    hreflangList,
  } = model;

  const respGridModel = respGridUtil(model?.cqItems?.root?.[':items']?.responsivegrid, { normalizeTracking: true });

  return (
    <Layout
      metaNames={metaNames}
      metaProps={metaProperties}
      title={title}
      staticComponentClientLibsCSS={staticComponentClientLibsCSS}
      staticComponentClientLibsJS={staticComponentClientLibsJS}
      isAuthorMode={isAuthorMode}
      gridCss={gridCss}
      canonical={canonical}
      hreflangList={hreflangList}
    >
      <section>
        <div className={clsx(Types.Themes.Alternate)}>
          <ResponsiveGrid
            key={pagePath}
            {...respGridModel}
            model={respGridModel}
            pagePath={pagePath}
            itemPath="root/responsivegrid"
          />
        </div>
      </section>
    </Layout>
  );
}

const cobrandSpecificData = async (context: any, parallelCallsArr: any) => {
  const query = new URLSearchParams(context.query);
  let showError = false;

  const ecmResult = await checkEcmEligibility(context, parallelCallsArr, {
    operationSignatures,
    apolloClientUtils: {
      getNextMiApolloClient,
      getDeployedEnvType,
    },
    phoenixCobrandECMQuery,
  });

  // Enhanced logging for debugging
  log.debug(
    `[rendererlog] ECM Result: ${JSON.stringify({
      hasEligibleCard: ecmResult.hasEligibleCard,
      selectedCardCode: ecmResult.selectedCardCode,
      selectedCardParam: ecmResult.selectedCardParam,
      userCards: ecmResult.userCards,
      hasError: !!ecmResult.error,
    })}`
  );

  if (ecmResult.error) {
    log.error(`[rendererlog] GraphQL API call failed, error: ${JSON.stringify(ecmResult.error)}`);
  }

  if (query.get('error') && query.get('error') === 'true') {
    showError = true;
  }

  if (ecmResult.productSpecificSeoUrl) {
    log.debug(`[rendererlog] Fetching AEM content from: ${ecmResult.productSpecificSeoUrl}`);
  } else {
    log.debug(
      `[rendererlog] No eligible card detected - hasEligibleCard: ${ecmResult.hasEligibleCard}, selectedCardParam: ${ecmResult.selectedCardParam}`
    );
  }

  return {
    operationSignatures: [],
    NEXT_PUBLIC_FETCH_OFFERS_API_URL: process.env.NEXT_PUBLIC_FETCH_OFFERS_API_URL,
    nextPublicEnvKeys: [...Object.keys(envVarKeys)],
    showError,
    hasEligibleCard: ecmResult.hasEligibleCard,
    selectedCardParam: ecmResult.selectedCardParam,
    productSpecificSeoUrl: ecmResult.productSpecificSeoUrl, // This tells prePageCall which AEM page to fetch
    token: parallelCallsArr?.sessionData?.cacheData?.data?.accessToken,
    ecmError: ecmResult.error, // Pass error through for debugging
  };
};

const mockModel = async () => {
  if (process.env['NODE_ENV'] === 'development') {
    // nx-ignore-next-line
    const { creditCardsPageModel } = await import('../_localDevMocks/credit-cards-page.model'); //Load mock model dynamically only for dev mode
    return creditCardsPageModel;
  }
  return {};
};

const postAEMModelOperations = async (...args: any[]) => {
  const [context, , , model] = args;
  const userAgentHeader = context?.req?.headers?.['user-agent'] || '';
  const isMetaInAppUA = isMetaInAppUserAgent(userAgentHeader);
  const updatedModel = transformCobrandInterstitialForMetaInApp(model, isMetaInAppUA);
  const enableWebSDK = String(updatedModel?.enableWebSDK);
  const ENABLE_COBRAND_ECM = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_COBRAND_ENABLE_ECM, {
    target: TARGETS.APP.COBRAND_RENDERER,
  });
  if (enableWebSDK === 'true' && process.env['NEXT_PUBLIC_ENABLE_WEBSDK'] === 'true') {
    const ENABLE_CLIENT_DATA_LAYER = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_RTP_ENABLE_ACDL, {
      target: TARGETS.APP.COBRAND_RENDERER,
    });
    return {
      ...updatedModel,
      ENABLE_ACDL: ENABLE_CLIENT_DATA_LAYER?.experience_mcom_rtp_enable_acdl,
      ENABLE_ECM: ENABLE_COBRAND_ECM?.experience_mcom_cobrand_enable_ecm,
    };
  } else {
    return {
      ...updatedModel,
      ENABLE_ACDL: false,
      ENABLE_ECM: false,
    };
  }
};

const wrappedPrePageCall = prePageCall(
  cobrandSpecificData,
  mockModel,
  false,
  false,
  false,
  undefined,
  postAEMModelOperations
);

//SSR Render

export const getServerSideProps: GetServerSideProps<any> = async context => {
  const result = await wrappedPrePageCall(context);

  // Enhanced logging
  log.debug(
    `[rendererlog] getServerSideProps result: ${JSON.stringify({
      hasToken: !!result?.props?.token,
      hasEligibleCard: result?.props?.hasEligibleCard,
      selectedCardParam: result?.props?.selectedCardParam,
      productSpecificSeoUrl: result?.props?.productSpecificSeoUrl,
      hasEcmError: !!result?.props?.ecmError,
    })}`
  );

  // Redirect user to the ECM page if logged in with an eligible card
  if (result?.props?.token && result?.props?.hasEligibleCard === true) {
    const selectedCardParam = result?.props?.selectedCardParam;
    log.debug(`[rendererlog] Redirecting: User is authenticated and holds an eligible card (${selectedCardParam})`);

    const query = result?.props?.query;
    return buildRedirectResponse(ECM_ENDPOINT, query);
  }

  // Log if GraphQL API failed but user might have a card
  if (result?.props?.ecmError) {
    log.warn(
      `[rendererlog] GraphQL API error detected, user may have eligible card but cannot be verified: ${JSON.stringify(
        result.props.ecmError
      )}`
    );
  }

  return result;
};
